/**
 * Keycloak adapter service
 */

import Keycloak from 'keycloak-js'

/**
 * KeycloakAdapterSvc service
 * do init, login and refresh token for keycloak
 *
 * @class keycloakAdapterSvc
 * @memberof mwApp/auth
 * @see mwApp/auth
 *
 * @ngdoc service
 * @name mwApp/keycloak-adapter.service:keycloakAdapterSvc
 */

/* @ngInject */
export function KeycloakAdapterSvc ($q, storageSvc) {
  /* === private === */

  const storage = storageSvc.local // use localStorage to save session data

  /**
   * Keycloak object
   *
   * @private
   * @memberof mwApp/keycloak-adapter.service:KeycloakAdapterSvc~
   */
  let instance = null

  /**
   * Flag who informed about authorization in keycloak
   *
   * @private
   * @memberof mwApp/keycloak-adapter.service:KeycloakAdapterSvc~
   */
  let authorized = false

  /* === public === */
  this.init = init
  this.inited = inited
  this.setAuthHeader = setAuthHeader
  this.isAuthorized = isAuthorized
  this.setAuthorized = setAuthorized
  this.getToken = getToken
  this.logout = logout

  /* === implementation === */

  /**
   * Init Keycloak instance service
   *
   * @memberof mwApp/keycloak-adapter.service:keycloakAdapterSvc
   *
   * @param  {String} _url - keycloak server url
   * @param  {String} _realm - keycloak realm
   * @param  {String} _clientId - keycloak client ID
   * @param  {String} _redirectUrl - url for page to redirect after login
   * @param  {String} _go
   * @return {Promise} promise
   */
  function init (_url, _realm, _clientId, _redirectUrl, _go) {
    const goParam = _go ? `?go=${_go}` : ''
    const redirectUrl = `${_redirectUrl}${goParam}`
    const _keycloak = new Keycloak({
      url: _url,
      realm: _realm,
      clientId: _clientId
    })
    instance = _keycloak
    subscribeToKeycloakEvents(_keycloak)
    return _keycloak.init(
      {
        onLoad: 'login-required',
        redirectUri: redirectUrl,
        checkLoginIframe: false
      })
  }

  /**
   * Returning authorized state
   *
   * @memberof mwApp/keycloak-adapter.service:keycloakAdapterSvc
   * @return {Boolean}
   */
  function isAuthorized () {
    return !!authorized
  }

  /**
   * Returning inited adapter state
   *
   * @memberof mwApp/keycloak-adapter.service:keycloakAdapterSvc
   * @return {Boolean}
   */
  function inited () {
    return !!(instance)
  }

  /**
   * Returning inited adapter state
   *
   * @memberof mwApp/keycloak-adapter.service:keycloakAdapterSvc
   * @param  {Boolean} auth - authorized state value
   *
   */
  function setAuthorized (auth) {
    authorized = auth
  }

  /**
   * Subscription for auto refresh token
   *
   * @memberof mwApp/keycloak-adapter.service:keycloakAdapterSvc
   *
   */
  function subscribeToKeycloakEvents () {
    instance.onTokenExpired = () => {
      instance.updateToken(-1).then(function () {
        const token = getToken()
        storage.set('KEYCLOAK_TOKEN', token)
      })
    }
  }

  /**
   * Add authorize header in request
   *
   * @memberof mwApp/keycloak-adapter.service:keycloakAdapterSvc
   * @param  {Object} config - http request config
   * @return {Boolean}
   *
   */
  function setAuthHeader (config) {
    if (config) {
      let token
      if (authorized) {
        token = instance.token
      } else {
        token = storage.get('KEYCLOAK_TOKEN') || ''
      }
      config.headers.Authorization = 'Bearer ' + token
    }
  }

  /**
   * Get keycloak access token
   *
   * @memberof mwApp/keycloak-adapter.service:keycloakAdapterSvc
   * @return {String}
   *
   */
  function getToken () {
    if (instance && authorized) {
      return instance.token
    }
  }

  /**
   * Method for logout from keycloak
   *
   * @memberof mwApp/keycloak-adapter.service:keycloakAdapterSvc
   * @return {Promise}
   *
   */
  function logout () {
    authorized = false

    storage.remove('KEYCLOAK_REALM')
    storage.remove('KEYCLOAK_CLIENT_ID')
    storage.remove('KEYCLOAK_URL')

    storage.remove('authenticated')
    storage.remove('user')
    storage.remove('KEYCLOAK_TOKEN')
    const redirectUri = window.location.href.indexOf('logout') !== -1 ? window.location.origin : window.location.href
    return instance.logout({ redirectUri })
  }
}
